import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import RestaurantHomeCard from "./RestaurantHomeCard";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { base_url } from "Backend/api";
import toast from "react-hot-toast";
import AppSpinner from "components/ui/AppSpinner";
import FavoriteToggleDialog, {
  useFavoriteToggleDialog,
} from "components/favorites/FavoriteToggleDialog";
import {
  emitFavoriteToggled,
  subscribeFavoriteToggled,
} from "utils/favoriteEvents";

function listFromHomeFeedResponse(json) {
  if (!json) return [];
  if (Array.isArray(json)) return json;
  if (json.status && Array.isArray(json.data)) return json.data;
  if (Array.isArray(json.data)) return json.data;
  return [];
}

const HomeRestaurantsFeedSection = ({ sectionId, title, feedKey }) => {
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState({});
  const { showDialog, dialogMessage, showFavoriteDialog } =
    useFavoriteToggleDialog();

  const fetchList = useCallback(
    async (lat, lng, { showLoader = true } = {}) => {
      if (showLoader) setLoading(true);
      try {
        const token = localStorage.getItem("token");
        let url = `${base_url}/api/home/restaurants/${feedKey}`;
        const latStr = lat != null ? String(lat).trim() : "";
        const lngStr = lng != null ? String(lng).trim() : "";
        if (latStr && lngStr) {
          url += `?latitude=${encodeURIComponent(latStr)}&longitude=${encodeURIComponent(lngStr)}`;
        }

        const response = await fetch(url, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            Accept: "application/json",
          },
        });
        const result = await response.json();
        const list = listFromHomeFeedResponse(result);

        setRestaurants(list);
        const favState = {};
        list.forEach((r) => {
          if (r?.slug) favState[r.slug] = r.is_favorite || false;
        });
        setFavorites(favState);
      } catch (e) {
        console.error(e);
        setRestaurants([]);
      } finally {
        if (showLoader) setLoading(false);
      }
    },
    [feedKey]
  );

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      await fetchList("", "", { showLoader: true });

      if (!navigator.geolocation) return;

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (cancelled) return;
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          fetchList(lat, lng, { showLoader: false });
        },
        () => {},
        { enableHighAccuracy: false, timeout: 12000, maximumAge: 300000 }
      );
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [fetchList]);

  useEffect(() => {
    return subscribeFavoriteToggled(({ storeType, slug, isFavorite }) => {
      if (storeType !== "restaurant" || !slug) return;

      setFavorites((prev) => ({
        ...prev,
        [slug]: !!isFavorite,
      }));

      setRestaurants((prev) =>
        prev.map((restaurant) =>
          restaurant?.slug === slug
            ? { ...restaurant, is_favorite: !!isFavorite }
            : restaurant
        )
      );
    });
  }, []);

  const handleRestaurantClick = (slug) => {
    navigate(`/restaurant/${slug}`);
  };

  const handleToggleFavorite = async (slug) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${base_url}/api/favorites/restaurants/${slug}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();

      if (result.status) {
        const newFavState =
          typeof result?.data?.is_favorite === "boolean"
            ? result.data.is_favorite
            : !favorites[slug];
        setFavorites((prev) => ({
          ...prev,
          [slug]: newFavState,
        }));
        setRestaurants((prev) =>
          prev.map((restaurant) =>
            restaurant?.slug === slug
              ? { ...restaurant, is_favorite: newFavState }
              : restaurant
          )
        );
        emitFavoriteToggled({
          storeType: "restaurant",
          slug,
          isFavorite: newFavState,
        });
        showFavoriteDialog(newFavState);
      } else {
        toast.error(result.message || "حدث خطأ");
      }
    } catch (error) {
      console.error(error);
      toast.error("حدث خطأ في الشبكة");
    }
  };

  if (loading) {
    return (
      
      <section
        id={sectionId}
        className="w-full px-4 md:px-6 lg:px-8 py-2 scroll-mt-32"
      >
        <div className="max-w-7xl mx-auto">
          <h2 className="text-[14px] md:text-[16px] lg:text-[18px] font-bold text-foreground mb-2 font-[cairo]">
            {title}
          </h2>
          <div className="flex justify-center py-6">
            <AppSpinner />
          </div>
        </div>
      </section>
    );
  }

  if (restaurants.length === 0) return null;

  return (
    <>
    
    <section
      id={sectionId}
      className="w-full px-4 md:px-6 lg:px-8 py-2 scroll-mt-32"
    >
      <div className="max-w-7xl mx-auto">
        <h2 className="text-[14px] md:text-[16px] lg:text-[18px] font-bold text-foreground mb-2 font-[cairo]">
          {title}
        </h2>

        <Swiper
          modules={[Navigation, Pagination]}
          spaceBetween={6}
          slidesPerView={2.1}
          breakpoints={{
            640: { slidesPerView: 2.5 },
            768: { slidesPerView: 2.5 },
            1024: { slidesPerView: 3.5 },
          }}
          dir="rtl"
        >
          {restaurants.map((restaurant) => (
            <SwiperSlide key={restaurant.id ?? restaurant.slug}>
              <RestaurantHomeCard
                restaurant={restaurant}
                isFavorite={!!favorites[restaurant.slug]}
                onCardClick={handleRestaurantClick}
                onFavoriteClick={handleToggleFavorite}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
    <FavoriteToggleDialog open={showDialog} message={dialogMessage} />
    </>
  );
};

export default HomeRestaurantsFeedSection;
